import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { BaseComponent } from "../../../../../../../react-core/Html/BaseComponent";
import { Lightbox } from "../../../../../../../react-core/Html/Components/Lightbox";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { UserClaim } from "../../../../../../../Wrap/Constants/Enums/UserClaim";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { AuthenticationSystem } from "../../../../../../../Wrap/Models/Domains/Customer/AuthenticationSystem";
import { CheckAccountPermissionsResponse } from "../../../../../../../Wrap/Models/Domains/Customer/CheckAccountPermissionsResponse";
import { RestrictionReason } from "../../../../../../../Wrap/Models/Models/Account/Restrictions/RestrictionReason";
import { AdviserBridgePermissionCheck } from "../../../../../../../Wrap/Models/Models/Customer/AdviserBridgePermissionCheck";
import { NoteDetailModel } from "../../../../../../../Wrap/Models/Models/Customer/NoteDetailModel";
import { VulnerabilityDetail } from "../../../../../../../Wrap/Models/Models/Customer/VulnerabilityDetail";
import { SecurityUser } from "../../../../../../../Wrap/Models/Models/User/SecurityUser";
import CoachMarker from "../../../../../../Shared/CoachMarkers/CoachMarker";
import { Permission } from "../../../../../../Shared/Permission/Permission";
import { portfolioDashboardTabEnum } from "../../Resource/Content";
import { PortfolioDashboardMultiSelectionStore, PortfolioDashboardStore } from "../../Store/Variable";
import { PortfolioDashboardSummaryAction } from "../Summary/Action/Action";
import { MultiselectAccountsDropdown } from "./MultiselectAccountsDropdown";
import { MultiSelection } from "./MultiSelection";
import {
    headAccountGroupByInvestorType,
    isTradingAccount,
    selectedHeadAccounts,
    selectedHeadAccountsOnly,
} from "./PortfolioDashboardHelper";
import { PresentationContext } from "./PresentationViewWrapper";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";

interface IPortfolioDashboardHeaderProps {
    customerid: number;
    name: string;
    id: string;
    portfolioDashboardTabEnum: portfolioDashboardTabEnum;
    restrictionReasons: RestrictionReason[];
    vulnerabilityDetail: VulnerabilityDetail[];
    isAdviser: boolean;
    isAdmin: boolean;
    importantNotes: NoteDetailModel[];
    isConsolidate: boolean;
    isInternalUser: boolean;
}

interface IPortfolioDashboardHeaderState {
    selectedAccounts: string[];
    totalValue: number;
    allAccounts: ClientAccount[];
    openDropdown: boolean;
    clientDetail: SecurityUser;
    authenticationSystem: AuthenticationSystem;
    isUserGroupAllowed: boolean;
    isLightboxShow: boolean;
    isOnlineAccessAllowed: boolean;
    bridgeContent: JSX.Element;
}

export class PortfolioDashboardHeader extends BaseComponent<
    IPortfolioDashboardHeaderProps,
    IPortfolioDashboardHeaderState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            totalValue: 0,
            allAccounts: PortfolioDashboardStore.getData(),
            selectedAccounts: PortfolioDashboardMultiSelectionStore.getData(),
            openDropdown: false,
            clientDetail: null,
            authenticationSystem: null,
            isUserGroupAllowed: false,
            isLightboxShow: false,
            isOnlineAccessAllowed: false,
            bridgeContent: null,
        };
        this.subscribeToStore(PortfolioDashboardStore, () => this.onDashboardStoreChange());
        this.subscribeToStore(PortfolioDashboardMultiSelectionStore, () => this.onStoreChange());
    }

    private onDashboardStoreChange() {
        this.setState({ allAccounts: PortfolioDashboardStore.getData() });
        this.getHeaderTotalValue(PortfolioDashboardStore.getData());
    }

    private onStoreChange() {
        this.setState({ selectedAccounts: PortfolioDashboardMultiSelectionStore.getData() });
    }

    componentDidMount() {

        if (PortfolioDashboardStore.getData()) {
            if (!isTradingAccount(PortfolioDashboardStore.getData())) {
                this.getHeaderTotalValue(PortfolioDashboardStore.getData());
            }

            if (!this.props.isConsolidate) {
                this.getLastLoginDate(this.props.customerid);
            }
        }
    }

    private getHeaderTotalValue(allAccount: ClientAccount[]) {
        const headAccounts = (allAccount || []).map((ele) => ele.HierarchyId);
        const subAccount = allAccount.reduce((subAccounts, curAccount) => {
            return subAccounts.concat((curAccount.SubAccounts || []).map((account) => account.HierarchyId));
        }, []);

        PortfolioDashboardSummaryAction.getPortfolioValuationSummary(
            headAccounts,
            subAccount,
            null,
            null,
            JsonDateParser.toJsonDateNoOffset(new Date()),
            null,
            (val) => {
                this.setState({ totalValue: val.TotalPortfolioValue });
            }
        );
    }

    private getLastLoginDate(customerId: number) {
        PortfolioDashboardSummaryAction.getClientDetails(customerId, (res: AdviserBridgePermissionCheck) => {
            if (res != null) {
                this.setState({
                    clientDetail: res?.User,
                    authenticationSystem: res?.AuthenticationSystem,
                    isUserGroupAllowed: res?.IsClientPortalUserGroup,
                    isOnlineAccessAllowed: res?.User?.OSTEnabled || false,
                });
            }
        });
    }

    private isShowAdviserBridgeIcon() {
        // three conditionds to show icon
        // 1. authenticationSystem = 4
        // 2. CurrentUser is adviser
        // 3. User group is allowed
        // 4. Non consolidate account
        return (
            this.state.authenticationSystem == AuthenticationSystem.Okta &&
            this.props.isAdviser &&
            this.state.isUserGroupAllowed &&
            !this.props.isConsolidate
        );
    }


    lastLoginLabel = (numberOfDays: number) => {
        return <span>Client last logged in <b>{numberOfDays} days</b> ago</span>;
    };

    getSelectedHierarchyIdString(selectedAccounts: ClientAccount[]) {
        return selectedAccounts.map((selectedAccount) => selectedAccount.HierarchyId);
    }

    public render(): JSX.Element {
        const { isPresentationView } = this.context;
        const { selectedAccounts, allAccounts } = this.state;

        let formatMoney = (
            <div className="client-account-value">
                0<span className="decimal-place">.00</span>
            </div>
        );
        let date = null;
        if (this.state.totalValue) {
            const _formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(
                Math.abs(this.state.totalValue),
                CurrencyCode.GBP,
                true,
                2
            );
            const _arry = _formatMoney.split(".");

            date = JsonDateParser.getFormatDateString(new Date(), "dd MM yyyy", true, true);
            formatMoney = (
                <div className="client-account-value">
                    {_arry[0]}
                    <span className="decimal-place">.{_arry[1]}</span>
                </div>
            );
        }

        const diffDays = this.getDateDifference();
        const reasons = this.getRestrictionString();
        const groupAccount = headAccountGroupByInvestorType(
            selectedHeadAccounts(selectedAccounts || [], allAccounts || [])
        );
        return (
            <>
                <div className="flex-space-baseline mb-20">
                    <div className="row narrow-gutter">
                        <div className="col-auto">
                            {!isPresentationView && allAccounts?.length > 0 && (
                                <>
                                    <MultiselectAccountsDropdown
                                        cssWrapper="accounts control-size-460"
                                        id="uniqueID"
                                        title={this.props.name}
                                        placeholder={
                                            <>
                                                <span className="name">{this.props.name}</span>
                                                <span className="view">
                                                    {this.props.isConsolidate
                                                        ? "Consolidated View"
                                                        : selectedHeadAccountsOnly(selectedAccounts, allAccounts)?.length ==
                                                            allAccounts?.length
                                                            ? `Total client view`
                                                            : `Filtered view`}
                                                </span>
                                            </>
                                        }
                                        disable={[
                                            portfolioDashboardTabEnum.Transactions,
                                            portfolioDashboardTabEnum.Documents,
                                        ].includes(this.props.portfolioDashboardTabEnum)}
                                        isOpen={this.state.openDropdown}
                                        onToggle={(toggle) => {
                                            this.setState({ openDropdown: toggle });
                                        }}
                                    >
                                        <MultiSelection
                                            isConsolidated={this.props.isConsolidate}
                                            name={this.props.id}
                                            onClose={() => {
                                                this.setState({ openDropdown: false });
                                            }}
                                            portfolioDashboardTabEnum={this.props.portfolioDashboardTabEnum}
                                        />
                                    </MultiselectAccountsDropdown>
                                    <CoachMarker coachMarkerKey="client-centric-model" />
                                    <p className="text-muted text-size-14 mb-8">
                                        {this.props.isInternalUser && (
                                            <span>
                                                Firm: <b>{allAccounts[0]?.CompanyName}</b>{" "}
                                                <span className="pl-4 pr-4">|</span>
                                            </span>
                                        )}
                                        <span>
                                            Adviser: <b>{allAccounts[0]?.AdviserName}</b>
                                        </span>
                                    </p>
                                    <p className="text-muted text-size-14 mb-8">{this.props.isConsolidate ? "Account ID" : "Client ID"}: <b>{this.props.customerid}</b></p>
                                    {selectedHeadAccountsOnly(selectedAccounts || [], allAccounts || [])?.length !==
                                        allAccounts?.length && (
                                            <>
                                                {groupAccount.Individual.length > 0 &&
                                                    groupAccount.Individual.map((clientaccount, index) => {
                                                        return (
                                                            <div key={index} className="mad chosen-item individual">
                                                                {`Individual account - ${clientaccount.HeadClAccountId
                                                                    } -${clientaccount.SubAccounts.filter((subAccount) =>
                                                                        selectedAccounts.includes(subAccount.HierarchyId)
                                                                    )
                                                                        .map(
                                                                            (ele) =>
                                                                                ` ${ele.ProductDisplayName} - ${ele.ClAccountId.split("-")[1]
                                                                                }`
                                                                        )
                                                                        .toString()}`}
                                                            </div>
                                                        );
                                                    })}
                                                {groupAccount.Joint.length > 0 &&
                                                    groupAccount.Joint.map((clientaccount, index) => {
                                                        return (
                                                            <div
                                                                key={index}
                                                                className="mad chosen-item joint"
                                                            >{`Joint account - ${clientaccount.HeadClAccountId
                                                                } -${clientaccount.SubAccounts.filter((subAccount) =>
                                                                    selectedAccounts.includes(subAccount.HierarchyId)
                                                                )
                                                                    .map(
                                                                        (ele) =>
                                                                            ` ${ele.ProductDisplayName} - ${ele.ClAccountId.split("-")[1]
                                                                            }`
                                                                    )
                                                                    .toString()}`}</div>
                                                        );
                                                    })}
                                                {groupAccount.Trust.length > 0 &&
                                                    groupAccount.Trust.map((clientaccount, index) => {
                                                        return (
                                                            <div
                                                                key={index}
                                                                className="mad chosen-item trust"
                                                            >{`Trust account - ${clientaccount.HeadClAccountId
                                                                } -${clientaccount.SubAccounts.filter((subAccount) =>
                                                                    selectedAccounts.includes(subAccount.HierarchyId)
                                                                )
                                                                    .map(
                                                                        (ele) =>
                                                                            ` ${ele.ProductDisplayName} - ${ele.ClAccountId.split("-")[1]
                                                                            }`
                                                                    )
                                                                    .toString()}`}</div>
                                                        );
                                                    })}
                                            </>
                                        )}
                                </>
                            )}
                            {!isPresentationView && allAccounts?.length == 0 && (
                                <>
                                    <h1>{this.props.name}</h1>
                                    <p className="text-muted text-size-14 mb-8">
                                        <p className="text-muted text-size-14 mb-8">
                                            {this.props.isInternalUser && (
                                                <span>
                                                    Firm: <b>{allAccounts[0]?.CompanyName}</b>{" "}
                                                    <span className="pl-4 pr-4">|</span>
                                                </span>
                                            )}
                                            <span>
                                                Adviser: <b>{allAccounts[0]?.AdviserName}</b>
                                            </span>
                                        </p>
                                        <b>Client ID: {this.props.customerid}</b>
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                    {allAccounts?.length > 0 && (
                        <div className="row">
                            <div className="col-right">
                                {!isTradingAccount(PortfolioDashboardStore.getData()) && (
                                    <Permission userClaim={UserClaim.AdviserPortalPerformanceSummaryCanView}>
                                        <>
                                            {formatMoney}
                                            <div className="text-muted text-size-14 mb-12">
                                                Total portfolio value as at <b>{date}</b>
                                            </div>
                                        </>
                                    </Permission>
                                )}
                                {!isPresentationView && (
                                    <div className="text-size-14">
                                        <CoachMarker coachMarkerKey="client-portal-link" />
                                        {this.isShowAdviserBridgeIcon() && (
                                            <button className="client-portal-view">
                                                <span
                                                    className="icon"
                                                    aria-hidden="true"
                                                    onClick={() => {
                                                        if (!this.state.isOnlineAccessAllowed) {
                                                            this.setState({ isLightboxShow: true });
                                                        } else {
                                                            this.checkAdviserBridgePermission(
                                                                this.state.clientDetail?.ClientId
                                                            );
                                                        }
                                                    }}
                                                ></span>
                                                {JsonDateParser.toDate(this.state.clientDetail?.LastLoginTime) !== null && this.lastLoginLabel(diffDays)}
                                            </button>
                                        )}
                                        {!this.isShowAdviserBridgeIcon() && (
                                            <>{JsonDateParser.toDate(this.state.clientDetail?.LastLoginTime) !== null && this.lastLoginLabel(diffDays)}</>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}


                    {this.state.isLightboxShow &&
                        !this.state.isOnlineAccessAllowed &&
                        this.renderClientPortalWarningMessage()}
                    {this.state.isLightboxShow &&
                        this.state.isOnlineAccessAllowed &&
                        this.renderAdviserBridge(this.state.bridgeContent)}
                </div>
                <div className="row">
                    <div className="col-12">
                        {!isPresentationView && reasons != "" && !this.props.isAdviser && (
                            <p className="text-negative text-size-14 mr-20 ">{reasons}</p>
                        )}
                        {!isPresentationView && this.props.vulnerabilityDetail?.length > 0 && this.props.isAdmin && (
                            <p className="text-negative text-size-14 mr-20 ">
                                {"Vulnerable customer warning present"}
                            </p>
                        )}
                        {!isPresentationView && this.props.importantNotes?.length > 0 && (
                            <p className="text-negative text-size-14 mr-20 ">{"Important notes added"}</p>
                        )}
                    </div>
                </div>
            </>
        );
    }

    private checkAdviserBridgePermission(clientId: number) {
        PortfolioDashboardSummaryAction.checkAdviserBridgeAccountPermission(
            this.props.customerid,
            (res: CheckAccountPermissionsResponse) => {
                if (res?.IsPermissionedToAccounts) {
                    const content = (
                        <Permission userClaim={UserClaim.AdviserPortalDluAdminBridgeCanView}>
                            <button
                                className="btn primary"
                                onClick={() => {
                                    window.open(`/ClientBase/AdminBridge/AdminBridge.aspx?BridgeClientId=${clientId}`);
                                }}
                            >
                                View Client Portal
                            </button>
                        </Permission>
                    );
                    this.setState({ isOnlineAccessAllowed: true, bridgeContent: content, isLightboxShow: true });
                } else {
                    const content = <p id={`js-permissionResultMessage-${clientId}`}>{res?.DisplayMessage}</p>;
                    this.setState({ isOnlineAccessAllowed: true, bridgeContent: content, isLightboxShow: true });
                }
            }
        );
    }

    private renderClientPortalWarningMessage() {
        return (
            <Lightbox
                show={this.state.isLightboxShow}
                title={"Warning"}
                unclosable={true}
                cssWrapper="is-default-lightbox is-narrow"
                onClose={() => { }}
            >
                <p>
                    You are accessing a Client Portal view of data view that has User Access disabled. This client
                    currently does not have access.
                </p>
                <button
                    className="btn primary"
                    onClick={() => {
                        this.checkAdviserBridgePermission(this.state.clientDetail?.ClientId);
                    }}
                >
                    OK
                </button>
                <button className="btn secondary" onClick={() => this.setState({ isLightboxShow: false })}>
                    Cancel
                </button>
            </Lightbox>
        );
    }

    private renderAdviserBridge(ele) {
        return (
            <Lightbox
                show={this.state.isLightboxShow}
                title={"Account permission check"}
                unclosable={false}
                cssWrapper="is-default-lightbox is-narrow"
                onClose={() => {
                    this.setState({ isLightboxShow: false });
                }}
            >
                {ele}
            </Lightbox>
        );
    }

    private getDateDifference(): number {
        const date1 = new Date();
        const lastLoginDate = this.state.clientDetail?.LastLoginTime || new Date();
        let date2 = JsonDateParser.toDate(lastLoginDate);
        if (date2 == null) {
            date2 = new Date();
        }
        const diffDays = Math.floor(
            (Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate()) -
                Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate())) /
            (1000 * 60 * 60 * 24)
        );
        return diffDays;
    }

    private getRestrictionString() {
        const { restrictionReasons } = this.props;
        let reasons = "";
        restrictionReasons.forEach((reason) => {
            if (reason.CanView) {
                if (reasons != "") {
                    reasons = reasons.concat("; ");
                }
                const accountRestriction = reason.ClAccountId + " - " + reason.ReasonName;
                reasons = reasons.concat(accountRestriction);
            }
        });
        return reasons;
    }
}
PortfolioDashboardHeader.contextType = PresentationContext;
